import { SdgId } from '@upright/common/model/output/scores/sdg-alignment/types';
import { SdgLensLayout, sdgLensLayout } from '.';
import { Bounds, Side } from '../../../../impact-lens-profile/common/layout/types';
import { toBounds } from '../../../../impact-lens-profile/common/layout/utils';
import { useCarouselLayout } from '../../../../impact-lens-profile/context';
import { useSdgLensContext } from '../use-sdg-lens-context';
import { useSdgUnitsMode } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/use-sdg-unit-mode';

export type SdgLensBottomAreaLayout = {
  getLegendAreaBounds: (side: Side) => Bounds;
};

export function useGetSdgLensBottomAreaLayout(): SdgLensBottomAreaLayout | undefined {
  const base = useCarouselLayout();
  const ctx = useSdgLensContext();
  const [unitsMode] = useSdgUnitsMode();

  // The bottom area doesn't really need data to calculate layouts, but sdgLensLayout wants data, and
  // refactoring it so that some values can be calculated without data seems costly. So we just don't calculate
  // anything here until we get data.
  if (!ctx || !ctx.data.sdgAlignments) return undefined;

  const sdgLayout = sdgLensLayout(base, ctx.data.sdgAlignments, unitsMode);

  const [fullArea, centerArea] = [base.fullArea(), base.centerArea()];
  const getBoxArea = sdgLayout.sdgBoxArea;

  return {
    getLegendAreaBounds: (side: Side) => getLegendAreaBounds({ fullArea, centerArea, getBoxArea, side }),
  };
}

function getLegendAreaBounds(params: {
  fullArea: Bounds;
  centerArea: Bounds;
  getBoxArea: SdgLensLayout['sdgBoxArea'];
  side: Side;
}): Bounds {
  const { x1, x2 } = getLegendAreaX(params.fullArea, params.getBoxArea, params.side);

  const { centerArea } = params;
  return toBounds({ x1, x2, y1: centerArea.y2 + 20, y2: centerArea.y2 + 50 });
}

function getLegendAreaX(fullArea: Bounds, getBoxArea: SdgLensLayout['sdgBoxArea'], side: Side) {
  const boxArea = getBoxArea(side, SdgId._1_No_Poverty);
  // The legend area spans from the outer edge of the lens to the inner side of the box
  switch (side) {
    case Side.LEFT:
      return { x1: fullArea.x1, x2: boxArea.x2 };
    case Side.RIGHT:
      return { x1: boxArea.x1, x2: fullArea.x2 };
  }
}
